<template>
  <div class="crl-revocation-list">
    <label class="rbz-label">{{ t('serverGroup.configPage.clientCaCrl.table.title') }}</label>
    <rbz-table
      ref="crlTableRef"
      :columns="columns"
      :table-data="tableData"
      :show-download-button="false"
      :show-new-button="false"
      :show-new-as-row="true"
      :show-edit-as-row="true"
      :show-delete-selected-button="true"
      :row-buttons="rowButtons"
      @delete-row-button-clicked="deleteCrlRows([$event.id])"
    >
      <template #tableResultsOverride="{ numberOfResults }">
        <span class="table-results rbz-text-regular">{{
          t('serverGroup.configPage.clientCaCrl.table.revocationListTitle', [numberOfResults])
        }}</span>
      </template>
      <template #statusCell="cellProps">
        <div class="status-cell-wrapper">
          <span
            class="status-cell-content"
            :class="cellProps.row.status === true ? 'valid' : 'invalid'"
            v-b-tooltip="
              cellProps.row.status !== true && cellProps.row.error ? cellProps.row.error : undefined
            "
          >
            {{
              cellProps.row.status === true
                ? t('serverGroup.configPage.clientCaCrl.table.valid')
                : t('serverGroup.configPage.clientCaCrl.table.invalid')
            }}
          </span>
        </div>
      </template>
      <template #customElement>
        <div class="table-buttons-wrapper">
          <!-- Add a new row to the table with input field for the URL-->
          <rbz-form-button
            :label="t('serverGroup.configPage.clientCaCrl.table.addRow')"
            variant="secondary"
            :classes="'add-row-button'"
            icon="IconAdd"
            @button-click="crlTableRef?.setShowNewRow(true)"
          />
        </div>
      </template>
      <template #newRowOverrideConfirmationButtons="newRowProps">
        <td class="row-buttons-cell">
          <form-confirmation
            @confirm="
              newRowProps.mode === 'new'
                ? addCrlRow(newRowProps.data as CrlRevocationListData)
                : editCrlRow(newRowProps.data as CrlRevocationListData)
            "
            :is-invalid="
              !isCrlRowUrlValid(
                newRowProps.data as CrlRevocationListData,
                newRowProps.mode === 'edit' ? newRowProps.data.id : undefined
              )
            "
            @cancel="
              newRowProps.mode === 'new' ? cancelNewRow() : cancelEditCrlRow(newRowProps.data.id)
            "
          />
        </td>
      </template>
      <template #newRowOverride="newRowProps">
        <td class="data-cell width-350px">
          <rbz-form-input
            :name="`new-row-url-input`"
            :classes="'new-row-url-input'"
            :data-qa="`new-row-url-input`"
            :placeholder="t('serverGroup.configPage.clientCaCrl.table.url')"
            :is-invalid="
              !isCrlRowUrlValid(
                newRowProps.data as CrlRevocationListData,
                newRowProps.mode === 'edit' ? newRowProps.data.id : undefined
              )
            "
            :tooltip="invalidCrlError"
            v-model:inputValue="newRowProps.data.url"
          />
        </td>
        <td class="data-cell width-250px ellipsis">
          <span class="data-cell-content">{{ newRowProps.data.issuer || '—' }}</span>
        </td>
        <td class="data-cell width-180px ellipsis">
          <span class="data-cell-content">{{ newRowProps.data.last_update || '—' }}</span>
        </td>
        <td class="data-cell width-180px ellipsis">
          <span class="data-cell-content">{{ newRowProps.data.last_download || '—' }}</span>
        </td>
        <td class="data-cell width-100px">
          <div class="status-cell-wrapper">
            <span
              class="status-cell-content"
              :class="newRowProps.data.status === true ? 'valid' : 'invalid'"
              v-b-tooltip="
                newRowProps.data.status !== true && newRowProps.data.error
                  ? newRowProps.data.error
                  : undefined
              "
            >
              {{
                newRowProps.data.status === true
                  ? t('serverGroup.configPage.clientCaCrl.table.valid')
                  : t('serverGroup.configPage.clientCaCrl.table.invalid')
              }}
            </span>
          </div>
        </td>
      </template>
      <template #actionsColumnHeader>
        <span>{{ t('serverGroup.configPage.clientCaCrl.table.actions') }}</span>
      </template>
    </rbz-table>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import FormConfirmation from 'ui-component-library/rbz/components/FormConfirmation.vue';
import { ref, watch } from 'vue';
import DatasetsUtils from 'ui-component-library/helpers/DatasetsUtils';
import type { CrlRevocationListData } from '@/types';

const props = defineProps<{
  crlList: CrlRevocationListData[];
}>();

const emit = defineEmits<{
  (e: 'validate:crlList', value: CrlRevocationListData[]): void;
  (e: 'update:crlList', value: CrlRevocationListData[]): void;
}>();

const { t } = useI18n();
const tableData = ref<CrlRevocationListData[]>([]);
const crlTableRef = ref<InstanceType<typeof RbzTable> | null>(null);
const invalidCrlError = ref<string>('');

const columns = [
  {
    id: 'url',
    title: t('serverGroup.configPage.clientCaCrl.table.url'),
    fieldNames: ['url'],
    cellContentClasses: 'ellipsis',
    isSortable: true,
    isSearchable: true,
    tooltipFunctionAlwaysDisplayed: true,
    tooltipFunction: (item: CrlRevocationListData) => {
      return item.url;
    },
  },
  {
    id: 'issuer',
    title: t('serverGroup.configPage.clientCaCrl.table.issuer'),
    fieldNames: ['issuer'],
    isSortable: true,
    cellContentClasses: 'ellipsis',
    isSearchable: true,
    tooltipFunctionAlwaysDisplayed: true,
    tooltipFunction: (item: CrlRevocationListData) => {
      return item.issuer;
    },
  },
  {
    id: 'last-updated',
    title: t('serverGroup.configPage.clientCaCrl.table.lastUpdate'),
    fieldNames: ['last_update'],
    isSortable: true,
    isSearchable: true,
    cellContentClasses: 'ellipsis',
    displayFunction: (item: CrlRevocationListData) => {
      if (!item.last_update) return '—';
      const date = new Date(item.last_update);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
  {
    id: 'last-download',
    title: t('serverGroup.configPage.clientCaCrl.table.lastDownload'),
    fieldNames: ['last_download'],
    isSortable: true,
    isSearchable: true,
    cellContentClasses: 'ellipsis',
    displayFunction: (item: CrlRevocationListData) => {
      if (!item.last_download) return '—';
      const date = new Date(item.last_download);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
  {
    id: 'status',
    title: t('serverGroup.configPage.clientCaCrl.table.status'),
    fieldNames: ['status'],
    isSortable: true,
    displaySlotName: 'statusCell',
    displayFunction: (item: CrlRevocationListData) => {
      return item.status === true
        ? t('serverGroup.configPage.clientCaCrl.table.valid')
        : t('serverGroup.configPage.clientCaCrl.table.invalid');
    },
    isSearchable: true,
  },
] as RbzTableColumnOptions[];

const rowButtons = [
  {
    id: 'edit',
    tooltip: t('serverGroup.configPage.clientCaCrl.table.editButtonTooltip'),
    icon: 'IconEdit',
    editInline: true,
  },
  {
    id: 'delete',
    tooltip: t('serverGroup.configPage.clientCaCrl.table.deleteButtonTooltip'),
    icon: 'IconDelete',
  },
] as RbzTableRowButton[];

watch(
  () => props.crlList,
  (list) => {
    tableData.value = (list || []).map((row) => ({ ...row }));
  },
  { immediate: true }
);

function addCrlRow(data: CrlRevocationListData) {
  const url = data?.url?.trim();
  if (!isCrlRowUrlValid(data)) return;

  const crlData = [
    ...tableData.value,
    {
      id: DatasetsUtils.generateUUID2(),
      url,
      issuer: '',
      last_update: '',
      last_download: '',
      status: false,
      error: '',
    },
  ];
  tableData.value = crlData;
  emit('update:crlList', crlData);
  emit('validate:crlList', crlData);
  cancelNewRow();
}

const editCrlRow = (data: CrlRevocationListData) => {
  const index = tableData.value.findIndex((row) => row.id === data?.id?.trim());
  const url = data?.url?.trim();
  if (!isCrlRowUrlValid(data, data.id)) return;

  const normalizedUrl = normalizeUrl(url);
  const isDuplicate = tableData.value.some(
    (row) => normalizeUrl(row.url?.trim() || '') === normalizedUrl && row.id !== data.id
  );
  if (isDuplicate) return;

  tableData.value = tableData.value.map((row, i) =>
    i === index
      ? {
          id: data.id,
          url,
          issuer: data.issuer || '',
          last_update: data.last_update || '',
          last_download: data.last_download || '',
          status: data.status || false,
          error: data.error || '',
        }
      : row
  );
  emit('update:crlList', tableData.value);
  emit('validate:crlList', tableData.value);
  cancelEditCrlRow(data.id);
};

function cancelNewRow() {
  crlTableRef.value?.setShowNewRow(false);
}

const cancelEditCrlRow = (id?: string) => {
  if (crlTableRef.value && id) {
    delete crlTableRef.value.rowsInEditMode[id];
  }
};

const isUrlValid = (url: string): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const normalizeUrl = (url: string): string => {
  return url?.replace(/\/+$/, '') || '';
};

const getUrlValidationError = (
  row: CrlRevocationListData | undefined,
  excludeId?: string
): string => {
  const url = row?.url?.trim();
  if (!url) return '';
  const normalizedUrl = normalizeUrl(url);
  const isDuplicate = tableData.value.some(
    (r) => normalizeUrl(r.url?.trim() || '') === normalizedUrl && r.id !== excludeId
  );
  if (isDuplicate) return t('serverGroup.configPage.clientCaCrl.table.duplicateUrl');
  if (!isUrlValid(url)) return t('serverGroup.configPage.clientCaCrl.table.invalidUrlFormat');
  return '';
};

const isCrlRowUrlValid = (row: CrlRevocationListData | undefined, excludeId?: string): boolean => {
  const error = getUrlValidationError(row, excludeId);
  invalidCrlError.value = error;
  return !error;
};

const deleteCrlRows = (ids: string[]) => {
  tableData.value = tableData.value.filter((row) => !ids.includes(row.id));
  emit('update:crlList', tableData.value);
};
</script>

<style scoped lang="scss">
@use 'sass:map';
@use 'ui-component-library/assets/styles/colors' as colors;

/* Local layout and spacing (exact values preserved for UX) */
$crl-padding: 10px;
$crl-section-padding: 16px;
$crl-gap-tight: 8px;
$crl-radius: 4px;
$crl-button-size: 28px;
$crl-cell-padding-x: 12px;
$crl-sort-arrow-size: 12px;
$crl-status-pill-width: 60px;

/* --- Component layout --- */
.crl-revocation-list {
  padding: $crl-section-padding 0;

  .table-buttons-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: $crl-padding;
  }

  :deep(.rbz-table-wrapper:has(.rbz-table.filters-active)) {
    .top-navbar .filter-table-data-button {
      background-color: map.get(colors.$colors, 'honeydew-light');
      border-radius: $crl-radius;

      .filter-inactive,
      .button-icon {
        fill: map.get(colors.$colors, 'jewel');
      }
    }
  }

  :deep(.row-buttons .rbz-button.tertiary .button-icon) {
    fill: map.get(colors.$colors, 'manatee');
  }

  :deep(.row-buttons .rbz-button.tertiary:hover .button-icon) {
    fill: map.get(colors.$colors, 'jewel');
  }
}

.rbz-table-wrapper {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid map.get(colors.$colors, 'honeydew-light');
  border-radius: 4px;
}

.crl-revocation-list :deep(.filter-table-data-button .rbz-button:hover) {
  background-color: map.get(colors.$colors, 'mountain-meadow-light') !important;
}

.crl-revocation-list :deep(.addition-row) {
  background-color: map.get(colors.$colors, 'blue-tint');
}

/* --- Table: header, rows, cells --- */
.crl-revocation-list :deep(.rbz-table) {
  th {
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    letter-spacing: 0;
    font-style: normal;
    color: map.get(colors.$colors, 'storm-gray');
    padding: 8px $crl-cell-padding-x;
    height: 35px;
  }

  tr:hover {
    background-color: map.get(colors.$colors, 'blue-tint');
  }

  .status-cell-content {
    display: flex;
    padding: 5px 3px;
    width: $crl-status-pill-width;
    justify-content: center;
    border-radius: $crl-radius;
    font-size: 11px;

    &.valid {
      background: map.get(colors.$colors, 'honeydew-light');
      color: map.get(colors.$colors, 'forest-green');
      border: 1px solid map.get(colors.$colors, 'honeydew-light');
    }

    &.invalid {
      background: map.get(colors.$colors, 'misty-rose-light');
      color: map.get(colors.$colors, 'firebrick');
      border: 1px solid map.get(colors.$colors, 'misty-rose-light');
    }
  }

  .data-cell .data-cell-content {
    padding: 0;
  }

  .rbz-button-wrapper .rbz-button.tertiary {
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.crl-revocation-list :deep(.rbz-table .filter-inactive) {
  fill: map.get(colors.$colors, 'manatee');
}

.crl-revocation-list :deep(.rbz-table .sort-arrow-up),
.crl-revocation-list :deep(.rbz-table .sort-arrow-down) {
  height: $crl-sort-arrow-size;
  width: $crl-sort-arrow-size;
}

.crl-revocation-list :deep(.rbz-table .data-cell) {
  color: map.get(colors.$colors, 'bright-gray');
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 19.5px;
  letter-spacing: -0.076px;
  padding: 0 $crl-cell-padding-x;
}

.crl-revocation-list :deep(.input-holder .form-input) {
  height: 30px;
  color: map.get(colors.$colors, 'shuttle-gray');
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.crl-revocation-list :deep(.table-results.rbz-text-regular) {
  color: map.get(colors.$colors, 'gunmetal');
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 19.5px;
  letter-spacing: -0.076px;
}

.crl-revocation-list :deep(.sort-arrow-up.color-fill-shuttle-gray),
.crl-revocation-list :deep(.sort-arrow-down.color-fill-shuttle-gray) {
  width: $crl-sort-arrow-size;
  height: $crl-sort-arrow-size;
}

.crl-revocation-list :deep(th:hover) {
  font-weight: 700;
}

/* --- Buttons: secondary (add row), tertiary (row actions), add-row --- */
.crl-revocation-list :deep(.rbz-button.secondary) {
  border: 0;
  box-shadow: none;
  color: map.get(colors.$colors, 'jewel');
  text-decoration: none;
  font-size: 13px;

  .button-icon {
    width: 14px;
    height: 14px;
  }

  &:hover {
    border: 0;
    box-shadow: none;
    text-decoration: underline;
    background-color: transparent;
    color: map.get(colors.$colors, 'jewel');
    padding: 7px 11px;
  }

  &:focus {
    border: 0;
    box-shadow: none;
  }
}

.crl-revocation-list :deep(.add-row-button) {
  &:hover,
  &:focus,
  &:active {
    background-color: map.get(colors.$colors, 'white');

    .button-icon {
      fill: map.get(colors.$colors, 'jewel');
    }
  }
}

.crl-revocation-list :deep(.rbz-button-wrapper) {
  display: flex;
  align-items: center;
  border: 0;

  .rbz-button.tertiary {
    height: $crl-button-size;
    width: $crl-button-size;
    padding: 6px;
    display: flex;
    justify-content: center;
    background-color: transparent;

    .button-icon {
      width: 24px;
      height: 24px;
    }
  }
}

.crl-revocation-list :deep(.rbz-button.secondary.bulk-add-button .button-icon) {
  fill: none;
}

/* --- Confirmation (FormConfirmation in row) --- */
.crl-revocation-list :deep(.confirmation-holder) {
  gap: $crl-gap-tight;
  justify-content: end;
}

.crl-revocation-list :deep(.confirm-button-wrapper),
.crl-revocation-list :deep(.cancel-button-wrapper) {
  height: $crl-button-size;
  width: $crl-button-size;
}

.crl-revocation-list :deep(.svg-rectangle) {
  stroke: map.get(colors.$colors, 'jewel');
}

.crl-revocation-list :deep(.svg-check),
.crl-revocation-list :deep(.svg-cancel) {
  fill: map.get(colors.$colors, 'jewel');
}
</style>
